import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlarmClock, Bell, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Global interview alarm — fires N minutes before a scheduled interview.
 *
 * All 3 knobs are user-tuneable from the Bookings Dashboard settings panel
 * (localStorage-backed so each admin can configure their own preference
 * without touching global state):
 *   - `adelina_alarm_lead_min`   → 1–30 (default 5)   how early to fire
 *   - `adelina_alarm_sound`      → "on" | "off"        beeper on save
 *   - `adelina_alarm_poll_sec`   → 15–120 (default 30) network poll interval
 */
const FIRED_KEY = "adelina_alarm_fired_v1";
export const ALARM_LEAD_KEY  = "adelina_alarm_lead_min";
export const ALARM_SOUND_KEY = "adelina_alarm_sound";
export const ALARM_POLL_KEY  = "adelina_alarm_poll_sec";

function readInt(key, def, lo, hi) {
  const raw = parseInt(localStorage.getItem(key) || "", 10);
  if (Number.isNaN(raw)) return def;
  return Math.max(lo, Math.min(hi, raw));
}

function getFired() {
  try { return new Set(JSON.parse(sessionStorage.getItem(FIRED_KEY) || "[]")); }
  catch { return new Set(); }
}
function setFired(set) {
  try { sessionStorage.setItem(FIRED_KEY, JSON.stringify([...set])); } catch (_e) { /* ignore */ }
}

function useBeep(soundOn) {
  const ctxRef = useRef(null);
  const loopRef = useRef(null);

  const start = () => {
    stop();
    if (!soundOn) return;   // silent mode — only visual modal
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = ctxRef.current || new Ctx();
      ctxRef.current = ctx;
      const play = () => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.6);
      };
      play();
      loopRef.current = setInterval(play, 1200);
    } catch (_e) { /* ignore audio errors */ }
  };

  const stop = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
  };

  useEffect(() => () => stop(), []);
  return { start, stop };
}

export default function InterviewAlarm() {
  const [active, setActive] = useState(null);
  const soundOn = (localStorage.getItem(ALARM_SOUND_KEY) || "on") !== "off";
  const { start, stop } = useBeep(soundOn);
  const activeRef = useRef(null);
  activeRef.current = active;

  const hasSession = () =>
    !!(localStorage.getItem("adelina_token") || localStorage.getItem("adelina_emp_token"));

  const tick = useCallback(async () => {
    if (!hasSession()) return;
    const leadMin = readInt(ALARM_LEAD_KEY, 5, 1, 30);
    try {
      // Query a slightly larger window than the lead time so we still catch
      // an interview that ticks over between polls.
      const { data } = await api.get("/bookings/upcoming", { params: { minutes: leadMin + 1 } });
      const rows = Array.isArray(data) ? data : [];
      const now = Date.now();
      const fired = getFired();
      for (const r of rows) {
        const sched = new Date(r.scheduled_at).getTime();
        const minutesAway = (sched - now) / 60000;
        if (minutesAway > leadMin || minutesAway < -1) continue;
        const key = `${r.booking_id}::${r.date}::${r.time}`;
        if (fired.has(key)) continue;
        if (activeRef.current) return;
        fired.add(key);
        setFired(fired);
        setActive({ ...r, minutesAway: Math.max(0, Math.round(minutesAway)) });
        start();
        return;
      }
    } catch (_err) { /* ignore poll errors */ }
  }, [start]);

  useEffect(() => {
    tick();
    const pollSec = readInt(ALARM_POLL_KEY, 30, 15, 120);
    const t = setInterval(tick, pollSec * 1000);
    return () => clearInterval(t);
  }, [tick]);

  const dismiss = () => {
    stop();
    setActive(null);
  };

  if (!active) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="alertdialog"
      aria-modal="true"
      data-testid="interview-alarm-modal"
    >
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl border border-[#b8860b]/40 animate-pulse-slow">
        <div className="bg-gradient-to-r from-red-900 via-red-700 to-red-900 text-white px-6 py-4 flex items-center gap-3">
          <AlarmClock size={22} className="animate-bounce" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Interview Reminder</div>
            <div className="font-serif text-xl">Starts in ~{active.minutesAway} min</div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm">
          <div className="flex items-center gap-2 text-zinc-800">
            <Bell size={14} className="text-[#b8860b]" />
            <span>An interview is about to begin.</span>
          </div>
          <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4 space-y-1.5">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Schedule</span>
              <div className="font-mono font-semibold text-zinc-950" data-testid="alarm-schedule">
                {active.date} at {active.time}
              </div>
            </div>
            {active.employer_name && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Employer</span>
                <div className="text-zinc-900" data-testid="alarm-employer">
                  {active.employer_name}
                  {active.employer_phone && (
                    <span className="text-zinc-500"> · {active.employer_phone}</span>
                  )}
                </div>
              </div>
            )}
            <div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Status</span>
              <div className="text-zinc-900 capitalize">{active.status}</div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={dismiss}
            className="btn-gold w-full inline-flex items-center justify-center gap-2"
            data-testid="alarm-dismiss-btn"
          >
            <X size={14} /> Dismiss alarm
          </button>
        </div>
      </div>
    </div>
  );
}
